package org.example.project.client

import kotlinx.browser.sessionStorage
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.Json
import org.example.project.client.error.SessionStorageException
import org.example.project.client.session.SessionStorage
import org.example.project.client.session.StoredSession

class JsSessionStorage(
    private val key: String = DEFAULT_SESSION_STORAGE_KEY,
    private val json: Json = Json {
        ignoreUnknownKeys = true
        isLenient = false
    }
) : SessionStorage {

    companion object {
        const val DEFAULT_SESSION_STORAGE_KEY = "apka_web_csrf_session"
    }

    override suspend fun save(session: StoredSession) {
        if (session !is StoredSession.Web) {
            throw SessionStorageException("Web session storage only accepts StoredSession.Web; Android session records are rejected")
        }

        val text = try {
            json.encodeToString(StoredSession.serializer(), session)
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            throw SessionStorageException("Failed to serialize session")
        }

        try {
            sessionStorage.setItem(key, text)
        } catch (e: CancellationException) {
            throw e
        } catch (_: Throwable) {
            throw SessionStorageException("Failed to write to browser sessionStorage")
        }
    }

    override suspend fun load(): StoredSession? {
        return try {
            val item = sessionStorage.getItem(key) ?: return null
            val decoded = json.decodeFromString(StoredSession.serializer(), item)
            if (decoded is StoredSession.Web) {
                decoded
            } else {
                try {
                    sessionStorage.removeItem(key)
                } catch (e: CancellationException) {
                    throw e
                } catch (_: Throwable) {}
                null
            }
        } catch (e: CancellationException) {
            throw e
        } catch (_: Throwable) {
            try {
                sessionStorage.removeItem(key)
            } catch (e: CancellationException) {
                throw e
            } catch (_: Throwable) {}
            null
        }
    }

    override suspend fun clear() {
        try {
            sessionStorage.removeItem(key)
        } catch (e: CancellationException) {
            throw e
        } catch (_: Throwable) {
            throw SessionStorageException("Failed to clear browser sessionStorage")
        }
    }
}
